import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import React, { useCallback, useEffect, useState } from "react";
import { AIConfiguration } from "@/pages/Messaging/Bots/types";
import { AIConfigsContext } from "@/context/AIConfigsContext";

export function AIConfigsProvider(props: { children: React.ReactNode }) {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const [aiConfigurations, setAiConfigurations] = useState<AIConfiguration[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.messaging.get("AIConfiguration/List");
      if (!resp.ok) {
        // Keep the last-known-good configs on a transient failure (e.g. a deploy slot swap) —
        // wiping to [] here made a mid-deploy blip look like AI config was completely gone (2026-07-08).
        return;
      }
      const result = await resp.json();
      setAiConfigurations(Array.isArray(result) ? result : []);
    } catch {
      // Network error / non-JSON response mid-outage — keep whatever was last loaded.
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  const remove = useCallback(
    async (config: AIConfiguration) => {
      // First attempt without force — backend returns 409 if active workflows reference this config
      const resp = await api.messaging.delete("AIConfiguration", { id: config.id });

      if (resp.status === 409) {
        let body: { message?: string; workflows?: { id: number; name: string }[] } = {};
        try { body = await resp.json(); } catch {}

        const workflowList = body.workflows?.map(w => `  • ${w.name}`).join("\n") ?? "";
        const confirmed = window.confirm(
          `⚠️ This AI configuration is used by ${body.workflows?.length ?? "one or more"} active workflow(s):\n\n${workflowList}\n\nDeleting it will break those workflows until they are updated to use a different AI configuration.\n\nDelete anyway?`
        );
        if (!confirmed) return;

        // Retry with force=true — backend will zero out the references and log each one
        await api.messaging.delete("AIConfiguration", { id: config.id, force: true });
      }

      const clone = [...aiConfigurations];
      clone.splice(aiConfigurations.indexOf(config), 1);
      setAiConfigurations(clone);
    },
    [aiConfigurations, api.messaging]
  );

  const add = useCallback(() => {
    const config: AIConfiguration = {
      id: 0,
      name: "New Configuration",
      prompt: "",
      tone: "",
      aiModel: "claude-3-5-haiku-20241022",
      language: "Auto Detect",
      replyLength: "Short",
      knowledgeRestriction: false,
      followUp: false,
      rateResponse: false,
      activityCheck: false,
      usageLimit: 0,
      usageLimitWarning: "",
      status: "crawling",
      dataUrls: [],
      deploymentName: "",
      vectorSearchMethod: "None",
      indexName: ""
    };
    setAiConfigurations(prev => [...prev, config]);
    return config;
  }, []);

  const update = useCallback(
    async (config: AIConfiguration, index: any) => {
      setAiConfigurations(prev => {
        const clone = [...prev];
        clone[index] = config;
        return clone;
      });
    },
    []
  );

  useEffect(() => {
    if (subscription?.enableAI === false) {
      setLoading(false);
      return;
    }
    fetch();
  }, [fetch, subscription?.enableAI]);

  return (
    <AIConfigsContext.Provider
      value={{ aiConfigurations, add, update, remove, loading }}
    >
      {props.children}
    </AIConfigsContext.Provider>
  );
}
